import React, { useCallback, useState } from 'react';
import { FlatList, RefreshControl, StyleSheet, Text, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppInput } from '../../components/AppInput';
import { AppScreen } from '../../components/AppScreen';
import { ConfigurationDeleteButton } from '../../components/ConfigurationDeleteButton';
import { EmptyState } from '../../components/EmptyState';
import { ErrorState } from '../../components/ErrorState';
import { ScreenHeader } from '../../components/ScreenHeader';
import { PERMISSIONS } from '../../constants/permissions';
import { routes, type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import { useConfigurationSoftDelete } from '../../hooks/useConfigurationSoftDelete';
import {
  deactivateWfhConfiguration,
  getWfhConfigurations,
} from '../../services/hrPolicyConfigurationService';
import type { WfhConfiguration } from '../../types/hrPolicyConfiguration';
import { isActiveConfigurationRecord } from '../../utils/configurationFilters';
import { normalizeApiError } from '../../api/apiError';

type Props = NativeStackScreenProps<RootStackParamList, 'WfhConfigurationList'>;

export function WfhConfigurationListScreen({ navigation }: Props) {
  const palette = useAppColors();
  const auth = useAuth();
  const canEdit =
    auth.hasRole('HR_ADMIN') && auth.hasPermission(PERMISSIONS.SETTINGS_UPDATE);
  const [items, setItems] = useState<WfhConfiguration[]>([]);
  const [search, setSearch] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const softDelete = useConfigurationSoftDelete();

  const load = useCallback(
    async (refresh = false) => {
      setRefreshing(refresh);
      try {
        const page = await getWfhConfigurations(search);
        setItems(page.items.filter(item => isActiveConfigurationRecord(item.isActive)));
        setError(null);
      } catch (caught) {
        setError(normalizeApiError(caught).message);
      } finally {
        setRefreshing(false);
      }
    },
    [search],
  );

  useFocusEffect(
    useCallback(() => {
      load().catch(() => undefined);
    }, [load]),
  );

  return (
    <AppScreen contentStyle={s.screen}>
      <ScreenHeader title="WFH configuration" onBack={navigation.goBack} />
      <View style={s.body}>
        <AppInput
          label="Search"
          value={search}
          onChangeText={setSearch}
          placeholder="Policy code or name"
        />
        <AppButton title="Search" variant="secondary" onPress={() => load()} />
        <AppButton
          title="Create WFH policy"
          onPress={() => navigation.navigate(routes.wfhConfigurationCreate)}
        />
        {error && items.length === 0 ? (
          <ErrorState message={error} onRetry={() => load()} />
        ) : (
          <FlatList
            data={items}
            keyExtractor={item => String(item.wfhPolicyId)}
            refreshControl={
              <RefreshControl refreshing={refreshing} onRefresh={() => load(true)} />
            }
            contentContainerStyle={s.list}
            ListEmptyComponent={
              <EmptyState
                title="No WFH policies configured"
                message="Create the first WFH policy for this organization."
              />
            }
            renderItem={({ item }) => (
              <AppCard style={s.card}>
                <View style={s.cardHeader}>
                  <Text style={[s.title, { color: palette.textPrimary, flex: 1 }]}>{item.policyName}</Text>
                  <ConfigurationDeleteButton
                    placement="inline"
                    entityLabel="WFH policy"
                    loading={softDelete.busy}
                    disabled={softDelete.busy}
                    onPress={() =>
                      softDelete.confirmAndDelete({
                        entityLabel: 'WFH policy',
                        recordName: item.policyName,
                        deleteRecord: async () => {
                          await deactivateWfhConfiguration(item);
                        },
                        onSuccess: () => {
                          load(true).catch(() => undefined);
                        },
                        errorMessage: caught =>
                          normalizeApiError(caught).message ||
                          'Unable to delete WFH policy.',
                      })
                    }
                  />
                </View>
                <Text style={[s.text, { color: palette.textSecondary }]}>{item.policyCode}</Text>
                <Text style={[s.value, { color: palette.primary }]}>
                  {item.maximumDaysPerMonth} days/month · {item.maximumDaysPerYear} days/year
                </Text>
                <Text style={[s.text, { color: palette.textSecondary }]}>
                  Consecutive: {item.maximumConsecutiveDays} · Notice: {item.minimumNoticeDays} day(s)
                </Text>
                {canEdit ? (
                  <AppButton
                    title="Edit"
                    variant="secondary"
                    accessibilityLabel={`Edit ${item.policyName}`}
                    onPress={() =>
                      navigation.navigate(routes.wfhConfigurationCreate, {
                        wfhPolicyId: item.wfhPolicyId,
                      })
                    }
                  />
                ) : null}
              </AppCard>
            )}
          />
        )}
      </View>
    </AppScreen>
  );
}

const s = StyleSheet.create({
  screen: { flex: 1, paddingBottom: 0 },
  body: { flex: 1, marginTop: spacing.lg, gap: spacing.sm },
  list: { flexGrow: 1, gap: spacing.sm, paddingBottom: spacing.xl },
  card: { gap: spacing.xs },
  cardHeader: { flexDirection: 'row', alignItems: 'flex-start', gap: spacing.sm },
  title: typography.sectionTitle,
  text: typography.body,
  value: { ...typography.body, fontWeight: '700' },
});
